/**
 * Planning Engine Context — multi-instance (Scenario × Input Scope).
 * Each instance’s latest output is persisted in planning_runs.
 */

import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import {
  engineRunBlockedReason,
  engineRunSavedNotice,
  RUN_NO_SCENARIO_REASON,
  RUN_NO_WORKSPACE_REASON,
} from './engineRunGate';
import {
  getScenarios,
  resolveItemIdsFromSource,
  resolveScenarioBoundEngineInput,
  bindScenarioInputSource,
  runEngineScoped,
  upsertPlanningRun,
  getPlanningRun,
  engineInstanceKey,
  engineInputLabel,
  reimportForceRanksFromPublishedList,
  resolveScopePlannability,
  type EngineInputSource,
  type PlanningRunRecord,
} from '../../../lib/planningEngineService';
import { shouldPreserveUserExplorationScope } from '../../../lib/planningEngineBoundScope';
import { mayUseLiveEngineInput, scenarioHasHardScope } from '../../../lib/scenarioHardScope';
import { useItemsStore } from '../../../stores/prioritization/itemsStore';
import { useItemRelationshipsStore } from '../../../stores/prioritization/itemRelationshipsStore';
import { useItemPoolsStore } from '../../../stores/prioritization/itemPoolsStore';
import type {
  Scenario,
  PlanningEngineOutput,
} from '../../../lib/planningEngineModule.types';
import type { ScopePlannability } from '../../../lib/planningScopePlannability';
import { useTenantStore } from '../../../stores/prioritization/tenantStore';

/** Safely extract a message string from any thrown value, including PostgrestError. */
function errorMessage(err: unknown): string {
  if (!err) return 'Unknown error';
  if (typeof err === 'string') return err;
  if (err instanceof Error) return err.message;
  const e = err as Record<string, unknown>;
  if (typeof e['message'] === 'string') return e['message'];
  if (typeof e['details'] === 'string') return e['details'];
  return JSON.stringify(err);
}

async function resolveScopeSet(engineInput: EngineInputSource): Promise<Set<string> | null> {
  if (engineInput.type === 'live') return null;

  const { ids, error } = await resolveItemIdsFromSource(engineInput);
  if (ids && ids.length > 0) return new Set(ids);

  if (engineInput.type === 'item_pool') {
    const localPool = useItemPoolsStore.getState().itemPools.find((p) => p.id === engineInput.id);
    if (localPool && localPool.priorityIds.length > 0) {
      return new Set(localPool.priorityIds);
    }
  }

  // Fail closed for named scopes — never widen to "all live items" on resolve miss.
  // Empty Set = hard empty scope (honest empty rails); null is reserved for live.
  if (error) {
    console.warn('[resolveScopeSet] failed; using empty scope', engineInput, error);
  }
  return new Set(ids ?? []);
}

interface PlanningEngineContextValue {
  projectId: string | null;
  scenarios: Scenario[];
  activeScenario: Scenario | null;
  planOutput: PlanningEngineOutput | null;
  /** When the active instance was last run (ISO), or null if never. */
  activeRunUpdatedAt: string | null;
  activeInstanceKey: string | null;
  activeInstanceLabel: string;
  loading: boolean;
  running: boolean;
  error: string | null;
  engineInput: EngineInputSource;
  setEngineInput: (source: EngineInputSource) => void;
  /**
   * Item IDs resolved from the active engineInput scope.
   * null = 'live' (no filtering).
   */
  scopedItemIds: Set<string> | null;
  /** True when the active scenario has a bound non-live Input Scope (Slice 0). */
  hardScoped: boolean;
  /**
   * Whether the bound scope is something the engine can read at all — effort,
   * not attention (PRD Part C §6.1). null while unresolved.
   */
  scopePlannability: ScopePlannability | null;
  /**
   * Why Run is unavailable right now (no workspace, no scenario, scope resolves
   * to 0 items), or null when it can run. Shown before the click.
   */
  runBlockedReason: string | null;
  /** Confirmation after the last successful run; cleared on the next run or scope change. */
  lastRunNotice: string | null;
  refreshScenarios: () => Promise<void>;
  /** Run the active Scenario × Scope instance (scoped, persisted). */
  runEngineForScenario: (scenarioId?: string) => Promise<void>;
  /** Alias — same as runEngineForScenario (scoped + persist). */
  runEngineWithLiveItems: () => Promise<void>;
  /** Refresh item fields from live store, then scoped run + persist. Does not wipe scenario membership. */
  runEngineWithSelectedInput: () => Promise<void>;
  /** Persist the current in-memory output to planning_runs without re-running. */
  saveActiveRun: () => Promise<void>;
  /**
   * Re-read the active Scenario × Scope result from planning_runs — after an
   * earlier run was made active again (restore_planning_run, 769).
   */
  reloadActiveRun: () => Promise<void>;
  setActiveScenario: (scenario: Scenario) => void;
  clearOutput: () => void;
}

const noopAsync = async () => {};

function fallbackContext(projectId: string | null): PlanningEngineContextValue {
  return {
    projectId,
    scenarios: [],
    activeScenario: null,
    planOutput: null,
    activeRunUpdatedAt: null,
    activeInstanceKey: null,
    activeInstanceLabel: 'All Live Items',
    loading: false,
    running: false,
    error: null,
    engineInput: { type: 'live' },
    setEngineInput: () => {},
    scopedItemIds: null,
    hardScoped: false,
    scopePlannability: null,
    runBlockedReason: projectId ? RUN_NO_SCENARIO_REASON : RUN_NO_WORKSPACE_REASON,
    lastRunNotice: null,
    refreshScenarios: noopAsync,
    runEngineForScenario: noopAsync,
    runEngineWithLiveItems: noopAsync,
    runEngineWithSelectedInput: noopAsync,
    saveActiveRun: noopAsync,
    reloadActiveRun: noopAsync,
    setActiveScenario: () => {},
    clearOutput: () => {},
  };
}

const PlanningEngineContext = createContext<PlanningEngineContextValue | null>(null);

export function PlanningEngineProvider({ children }: { children: React.ReactNode }) {
  const projectId = useTenantStore((s) => s.currentWorkspaceId);
  const fetchItems = useItemsStore((s) => s.fetchFromSupabase);
  const fetchRelationships = useItemRelationshipsStore((s) => s.fetchFromSupabase);

  const [scenarios, setScenarios] = useState<Scenario[]>([]);
  const [activeScenario, setActiveScenarioState] = useState<Scenario | null>(null);
  const [planOutput, setPlanOutput] = useState<PlanningEngineOutput | null>(null);
  const [activeRunUpdatedAt, setActiveRunUpdatedAt] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [engineInput, setEngineInputState] = useState<EngineInputSource>(() => {
    try {
      const stored = sessionStorage.getItem('planning-engine-input');
      if (stored) return JSON.parse(stored) as EngineInputSource;
    } catch { /* noop */ }
    return { type: 'live' };
  });
  const [scopedItemIds, setScopedItemIds] = useState<Set<string> | null>(null);
  const [scopeResolving, setScopeResolving] = useState(false);
  const [lastRunNotice, setLastRunNotice] = useState<string | null>(null);
  const [scopePlannability, setScopePlannability] = useState<ScopePlannability | null>(null);
  const items = useItemsStore((s) => s.items);

  // Synced immediately inside setEngineInput / applyBound (not only in an effect)
  // so first-load / in-flight bound-scope applies never read a stale "live".
  const engineInputRef = useRef(engineInput);
  useEffect(() => {
    engineInputRef.current = engineInput;
  }, [engineInput]);

  const activeInstanceKey = activeScenario
    ? engineInstanceKey(activeScenario.id, engineInput)
    : null;
  const activeInstanceLabel = engineInputLabel(engineInput);

  // Monotonic token guarding every write to planOutput. Async producers (the
  // persisted-instance load, the engine run) claim a slot and may only publish
  // if nothing newer has claimed one since. Without this, a getPlanningRun
  // issued before a run committed resolves *after* it and snaps the numbers
  // back to the pre-run record — the displayed values visibly flicker between
  // the old and new run until the in-flight reads drain.
  //
  // Declared here, above every consumer: several of the callbacks below name it
  // in their dependency arrays, which React evaluates during render, so a later
  // `const` would throw a temporal-dead-zone ReferenceError and crash the whole
  // provider on mount.
  const outputSeqRef = useRef(0);
  const claimOutputSlot = useCallback(() => (outputSeqRef.current += 1), []);

  // Same pattern for bound-scope applies: picking an Item Pool must invalidate
  // any resolveScenarioBoundEngineInput still in flight, or it snaps the picker
  // back to derived "Scenario priorities".
  const boundScopeSeqRef = useRef(0);
  const claimBoundScopeSlot = useCallback(() => (boundScopeSeqRef.current += 1), []);

  const writeEngineInput = useCallback(
    (source: EngineInputSource) => {
      engineInputRef.current = source;
      setEngineInputState(source);
      claimOutputSlot();
      setPlanOutput(null);
      setActiveRunUpdatedAt(null);
      setScopedItemIds(source.type === 'live' ? null : new Set());
      setScopeResolving(source.type !== 'live');
      setLastRunNotice(null);
      try {
        sessionStorage.setItem('planning-engine-input', JSON.stringify(source));
      } catch {
        /* noop */
      }
    },
    [claimOutputSlot],
  );

  const setEngineInput = useCallback(
    (source: EngineInputSource) => {
      // Slice 0: hard-scoped scenarios cannot escape to All Live Items.
      // Read sticky portfolio lock from the ref (previous value) so this
      // callback's identity does not change on every scope type flip — that
      // used to recreate refreshScenarios and re-fire scenario fetches.
      const locked =
        !mayUseLiveEngineInput(activeScenario) ||
        (activeScenario != null && engineInputRef.current.type === 'scenario_portfolio');
      if (source.type === 'live' && locked) {
        setError(
          'This scenario is a hard scope container — All Live Items is locked. Switch or unbind the scenario to explore the full workspace.',
        );
        return;
      }
      // Invalidate in-flight applyBoundScopeForScenario before writing.
      boundScopeSeqRef.current += 1;
      writeEngineInput(source);

      // Selecting a published list means "plan against this governed order" —
      // import its ranks into planning_priorities.force_rank now. Without this,
      // force_rank rows left over from a previous scope silently order the run
      // (demo finding F5). Deliberately only on user selection: scenario-bound
      // restores and reloads do not clobber manual re-ranks made afterwards.
      if (source.type === 'published_priority_list' && projectId) {
        void reimportForceRanksFromPublishedList(projectId, source.id).catch((err) => {
          console.warn('[PlanningEngineContext] force_rank re-import failed', err);
        });
      }

      // Persist named scopes on the scenario so resolveScenarioBoundEngineInput
      // restores Item Pool (etc.) instead of re-deriving scenario_portfolio.
      if (activeScenario && source.type !== 'live') {
        const scenarioId = activeScenario.id;
        const named = source;
        void bindScenarioInputSource(scenarioId, named)
          .then(() => {
            setActiveScenarioState((prev) => {
              if (!prev || prev.id !== scenarioId) return prev;
              return {
                ...prev,
                inputSourceType: named.type,
                inputSourceId: named.id,
                inputSourceName: named.name,
              };
            });
            setScenarios((list) =>
              list.map((s) =>
                s.id === scenarioId
                  ? {
                      ...s,
                      inputSourceType: named.type,
                      inputSourceId: named.id,
                      inputSourceName: named.name,
                    }
                  : s,
              ),
            );
          })
          .catch((err) => {
            console.warn('[PlanningEngineContext] bindScenarioInputSource failed', err);
          });
      }
    },
    [activeScenario, projectId, writeEngineInput],
  );

  // Effort vs attention: a list that ranks people to develop or risks to watch
  // is a good ranked list the engine has nothing to say about. Resolve the
  // verdict alongside the scope so the run views can explain instead of
  // reporting a zero-capacity failure.
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const verdict = await resolveScopePlannability({ engineInput, items, scopedItemIds });
        if (!cancelled) setScopePlannability(verdict);
      } catch (err) {
        console.warn('[PlanningEngineContext] scope eligibility check failed', err);
        if (!cancelled) setScopePlannability(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [engineInput, items, scopedItemIds]);

  // Persisted bind OR active scenario_portfolio scope — both lock live escape.
  const hardScoped =
    scenarioHasHardScope(activeScenario) ||
    (activeScenario != null && engineInput.type === 'scenario_portfolio');

  const applyRunRecord = useCallback((record: PlanningRunRecord | null, seq?: number) => {
    if (seq !== undefined && seq !== outputSeqRef.current) return;
    if (!record) {
      setPlanOutput(null);
      setActiveRunUpdatedAt(null);
      return;
    }
    setPlanOutput(record.output);
    setActiveRunUpdatedAt(record.updatedAt);
  }, []);

  const loadActiveInstance = useCallback(async () => {
    const seq = claimOutputSlot();
    if (!projectId || !activeScenario) {
      applyRunRecord(null, seq);
      return;
    }
    try {
      const record = await getPlanningRun(projectId, activeScenario.id, engineInput);
      applyRunRecord(record, seq);
    } catch (err) {
      console.warn('[PlanningEngineContext] failed to load planning run', err);
      applyRunRecord(null, seq);
    }
  }, [projectId, activeScenario, engineInput, applyRunRecord, claimOutputSlot]);

  const applyBoundScopeForScenario = useCallback(
    async (scenario: Scenario | null) => {
      if (!scenario) return;
      const seq = claimBoundScopeSlot();
      try {
        const bound = await resolveScenarioBoundEngineInput(scenario);
        if (seq !== boundScopeSeqRef.current) return;

        // User already picked an Item Pool (etc.) while resolve was in flight —
        // do not snap them back to derived "Scenario priorities".
        if (
          shouldPreserveUserExplorationScope(engineInputRef.current, bound, scenario)
        ) {
          return;
        }

        if (bound) {
          // Internal write: bypass setEngineInput sticky-live guard and avoid
          // re-binding / re-bumping the bound-scope generation token.
          writeEngineInput(bound);
          return;
        }
        // No bound scope for this scenario (e.g. a what-if hypothesis with no
        // owned priority rows yet) — reset to All Live Items rather than
        // silently keeping whichever scope the PREVIOUS scenario had. A
        // scenario_portfolio scope left over from a different scenario looks
        // identical in the UI but resolves to that OTHER scenario's priority
        // ids, so runs silently produce zero rows.
        writeEngineInput({ type: 'live' });
      } catch (err) {
        console.warn('[PlanningEngineContext] bound scope sync failed', err);
      }
    },
    [claimBoundScopeSlot, writeEngineInput],
  );

  const refreshScenarios = useCallback(async () => {
    if (!projectId) {
      setError('No workspace found. Please select a workspace first.');
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const list = await getScenarios(projectId);
      setScenarios(list);
      const active = list.find((s) => s.active) ?? list[0] ?? null;
      setActiveScenarioState((prev) => {
        const isFirstLoad = prev === null;
        // `prev` is always null on first mount, so comparing prev?.id to
        // active.id was always "changed" there — the branch below it (meant to
        // gate first-load rebinding on a stale "live" session value) could
        // never be reached, and this branch clobbered a restored Item Pool /
        // Collection / Queue / Published List scope back to the scenario's own
        // bound container on every page refresh. Only treat it as a genuine
        // scenario switch once something was actually loaded before.
        const switchedScenario = !isFirstLoad && prev.id !== (active?.id ?? null);
        if (switchedScenario && active) {
          void applyBoundScopeForScenario(active);
        } else if (isFirstLoad && active && engineInputRef.current.type === 'live') {
          // First load — only override a stale/default "live" session value
          // with the scenario's own bound container.
          void applyBoundScopeForScenario(active);
        }
        // getScenarios maps a brand-new object every call, so returning `active`
        // unconditionally always changes the reference — even when the row is
        // byte-identical. That re-renders, which rebuilds setEngineInput →
        // applyBoundScopeForScenario → refreshScenarios (a useCallback dep
        // chain), whose identity change re-fires this very effect: an endless
        // fetch loop, each iteration racing a fresh planning-run read. Keep the
        // previous object when nothing actually changed so the chain settles.
        if (prev && active && JSON.stringify(prev) === JSON.stringify(active)) return prev;
        return active;
      });
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setLoading(false);
    }
  }, [projectId, applyBoundScopeForScenario]);

  const setActiveScenario = useCallback((scenario: Scenario) => {
    setActiveScenarioState(scenario);
    setPlanOutput(null);
    setActiveRunUpdatedAt(null);
    void applyBoundScopeForScenario(scenario);
  }, [applyBoundScopeForScenario]);

  /**
   * Core run path: scoped in-memory engine + upsert planning_runs.
   * Does not replace scenario priority membership in DB.
   */
  const runActiveInstance = useCallback(async () => {
    if (!projectId) {
      setError('No workspace found.');
      return;
    }
    if (!activeScenario) {
      setError('No scenario selected. Create and activate a scenario first.');
      return;
    }

    setRunning(true);
    setError(null);
    setLastRunNotice(null);
    try {
      await Promise.all([
        fetchItems(),
        fetchRelationships(),
        useItemPoolsStore.getState().fetchFromSupabase(),
      ]);
      const items = useItemsStore.getState().items;
      const edges = useItemRelationshipsStore.getState().edges;

      // Always re-resolve scope on run — never trust a stale/null Set (fail-open
      // previously ran all live items when scope hadn't resolved yet).
      let scopeSet: Set<string> | null = null;
      if (engineInput.type !== 'live') {
        scopeSet = await resolveScopeSet(engineInput);
        setScopedItemIds(scopeSet);
        if (!scopeSet || scopeSet.size === 0) {
          setError(
            `Input scope “${engineInputLabel(engineInput)}” resolved to 0 items. ` +
              'Check that the queue/pool/collection has members, then try again.',
          );
          return;
        }
      } else {
        setScopedItemIds(null);
      }

      const output = await runEngineScoped({
        projectId,
        scenario: activeScenario,
        items,
        edges,
        scopedItemIds: scopeSet,
        scopeKind: engineInput.type,
        // Package / Competing groups ride along from the same store as `edges`,
        // so the run can refuse a plan that splits a package or commits to both
        // sides of a competing pair.
        relationshipGroups: useItemRelationshipsStore.getState().groups,
        relationshipGroupMemberships: useItemRelationshipsStore.getState().groupMemberships,
      });

      if (
        engineInput.type !== 'live' &&
        output.executiveSummary.totalWorkUnits > (scopeSet?.size ?? 0) * 2
      ) {
        // Sanity: priority-estimate mode ≈ 1 row/item; something skipped the filter.
        console.warn('[PlanningEngineContext] scoped run produced unexpected row count', {
          scopeSize: scopeSet?.size,
          totalWorkUnits: output.executiveSummary.totalWorkUnits,
        });
      }

      const record = await upsertPlanningRun({
        projectId,
        scenarioId: activeScenario.id,
        engineInput,
        scopedItemIds: scopeSet ? Array.from(scopeSet) : null,
        output,
      });
      // Claim immediately before publishing so the run — the user's explicit
      // action, holding the freshest data — always wins and invalidates any
      // load still in flight.
      applyRunRecord(record, claimOutputSlot());
      setLastRunNotice(engineRunSavedNotice(output.executiveSummary));
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setRunning(false);
    }
  }, [
    projectId,
    activeScenario,
    engineInput,
    fetchItems,
    fetchRelationships,
    applyRunRecord,
    claimOutputSlot,
  ]);

  const runEngineForScenario = useCallback(
    async (_scenarioId?: string) => {
      // scenarioId arg kept for API compat; active scenario + scope define the instance.
      await runActiveInstance();
    },
    [runActiveInstance],
  );

  const runEngineWithLiveItems = useCallback(async () => {
    await runActiveInstance();
  }, [runActiveInstance]);

  const runEngineWithSelectedInput = useCallback(async () => {
    // Same as Run — scoped + persist. Name kept for Overview "Sync & Run" button.
    await runActiveInstance();
  }, [runActiveInstance]);

  const saveActiveRun = useCallback(async () => {
    if (!projectId || !activeScenario || !planOutput) {
      setError('Run the engine first, then save results.');
      return;
    }
    setRunning(true);
    setError(null);
    try {
      const record = await upsertPlanningRun({
        projectId,
        scenarioId: activeScenario.id,
        engineInput,
        scopedItemIds: scopedItemIds ? Array.from(scopedItemIds) : null,
        output: planOutput,
      });
      applyRunRecord(record);
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setRunning(false);
    }
  }, [projectId, activeScenario, planOutput, engineInput, scopedItemIds, applyRunRecord]);

  const clearOutput = useCallback(() => {
    setPlanOutput(null);
    setActiveRunUpdatedAt(null);
  }, []);

  // Resolve scoped item IDs when input changes
  useEffect(() => {
    if (engineInput.type === 'live') {
      setScopedItemIds(null);
      setScopeResolving(false);
      return;
    }
    let cancelled = false;
    setScopeResolving(true);
    void resolveScopeSet(engineInput).then((scopeSet) => {
      if (cancelled) return;
      setScopedItemIds(scopeSet);
      setScopeResolving(false);
    });
    return () => { cancelled = true; };
  }, [engineInput]);

  // Load persisted instance when scenario or scope changes
  useEffect(() => {
    void loadActiveInstance();
  }, [loadActiveInstance]);

  useEffect(() => {
    void refreshScenarios();
  }, [refreshScenarios]);

  return (
    <PlanningEngineContext.Provider
      value={{
        projectId,
        scenarios,
        activeScenario,
        planOutput,
        activeRunUpdatedAt,
        activeInstanceKey,
        activeInstanceLabel,
        loading,
        running,
        error,
        engineInput,
        setEngineInput,
        scopedItemIds,
        hardScoped,
        scopePlannability,
        runBlockedReason: engineRunBlockedReason({
          projectId,
          hasActiveScenario: activeScenario != null,
          scopeIsLive: engineInput.type === 'live',
          scopeLabel: activeInstanceLabel,
          scopedItemIds,
          scopeResolving,
        }),
        lastRunNotice,
        refreshScenarios,
        runEngineForScenario,
        runEngineWithLiveItems,
        runEngineWithSelectedInput,
        saveActiveRun,
        reloadActiveRun: loadActiveInstance,
        setActiveScenario,
        clearOutput,
      }}
    >
      {children}
    </PlanningEngineContext.Provider>
  );
}

// eslint-disable-next-line react-refresh/only-export-components -- context file exports both Provider and hook
export function usePlanningEngine(): PlanningEngineContextValue {
  const ctx = useContext(PlanningEngineContext);
  const projectId = useTenantStore((s) => s.currentWorkspaceId);
  if (!ctx) {
    return fallbackContext(projectId);
  }
  return ctx;
}
